import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import AdminHeader from '../../components/AdminHeader';
import AudioControls from '../../components/AudioControls';
import './admin.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatScore(score) {
  if (score == null) return '—';
  return Number(score).toFixed(2);
}

function DesigBadge({ name }) {
  if (!name) return null;
  const cls = name === 'Winner'    ? 'badge badge--winner'
            : name === 'Runner-up' ? 'badge badge--runner-up'
            :                        'badge badge--submitted';
  return <span className={cls}>{name}</span>;
}

function RankBadge({ rank }) {
  const cls = rank <= 3 ? `a-rank a-rank--${rank}` : 'a-rank a-rank--other';
  return <span className={cls}>#{rank}</span>;
}

// The play + download buttons are the shared components/AudioControls.

// ─── AdminResults ─────────────────────────────────────────────────────────────
export default function AdminResults() {
  const [songs, setSongs]               = useState([]);
  const [categories, setCategories]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expanded, setExpanded]         = useState({}); // { song_id: bool }
  const [playingId, setPlayingId]       = useState(null); // song_id of currently playing audio

  useEffect(() => {
    Promise.all([
      api.get('/api/admin/results'),
      api.get('/api/admin/categories'),
    ])
      .then(([rRes, cRes]) => {
        setSongs(rRes.data.songs);
        setCategories(cRes.data.categories);
      })
      .catch(() => setError('Failed to load results.'))
      .finally(() => setLoading(false));
  }, []);

  function toggleExpand(id) {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleAudio(songId, shouldPlay) {
    // Stop any currently playing audio
    if (playingId && playingId !== songId) {
      const prev = document.getElementById(`audio-${playingId}`);
      if (prev) { prev.pause(); prev.currentTime = 0; }
    }
    const audio = document.getElementById(`audio-${songId}`);
    if (!audio) return;
    if (shouldPlay) {
      audio.play();
      setPlayingId(songId);
    } else {
      audio.pause();
      audio.currentTime = 0;
      setPlayingId(null);
    }
  }

  const filtered = categoryFilter
    ? songs.filter((s) => s.category_name === categoryFilter)
    : songs;

  // ── Loading ──
  if (loading) {
    return (
      <div className="c-page">
        <AdminHeader />
        <main className="c-main">
          <div className="c-loading">
            <div className="c-spinner" />
            <span>Loading results…</span>
          </div>
        </main>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className="c-page">
        <AdminHeader />
        <main className="c-main">
          <Link to="/admin/dashboard" className="c-back-btn">← Back to Dashboard</Link>
          <div className="c-alert" role="alert">{error}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="c-page">
      <AdminHeader />

      <main className="c-main">
        <div className="a-results-header">
          <div>
            <Link to="/admin/dashboard" className="c-back-btn">← Back to Dashboard</Link>
            <h1 className="a-results-title">Competition Results</h1>
          </div>
          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
            {filtered.length} song{filtered.length !== 1 ? 's' : ''} · ranked by weighted average score
          </div>
        </div>

        {/* ── Category filter ── */}
        <div className="a-filter-row">
          <label className="a-filter-label" htmlFor="cat-filter">Category:</label>
          <select
            id="cat-filter"
            className="c-input"
            style={{ width: 'auto', minWidth: '200px' }}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* ── Empty state ── */}
        {filtered.length === 0 ? (
          <div className="c-empty">
            <div className="c-empty__icon">🏆</div>
            <h3 className="c-empty__title">No results yet</h3>
            <p className="c-empty__text">
              {categoryFilter
                ? 'No songs match this category filter.'
                : 'Scores will appear here once judges submit evaluations.'}
            </p>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Song</th>
                  <th>Category</th>
                  <th>Contestant</th>
                  <th>Avg Score</th>
                  <th>Evaluations</th>
                  <th>Judges</th>
                  <th>Designations</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((song, idx) => (
                  <>
                    {/* ── Main song row ── */}
                    <tr key={song.id}>
                      <td>
                        <RankBadge rank={idx + 1} />
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <AudioControls
                            songId={song.id}
                            audioUrl={song.audio_url || null}
                            playingId={playingId}
                            onToggle={toggleAudio}
                          />
                          <div>
                            <strong>{song.title}</strong>
                            {song.artist && (
                              <span style={{ display: 'block', color: '#6b7280', fontSize: '0.8rem' }}>
                                {song.artist}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{song.category_name}</td>
                      <td>{song.contestant_name}</td>
                      <td>
                        <span className="a-score">
                          <span className="a-score__value">{formatScore(song.avg_weighted_score)}</span>
                        </span>
                      </td>
                      <td>
                        <span style={{
                          color: song.submitted_eval_count > 0 ? '#1a1a2e' : '#9ca3af',
                          fontWeight: song.submitted_eval_count > 0 ? '700' : '400',
                        }}>
                          {song.submitted_eval_count}
                        </span>
                        <span style={{ color: '#9ca3af' }}>/{song.total_assignments}</span>
                      </td>
                      <td>
                        {song.evaluations.length === 0 ? (
                          <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>—</span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                            {[...new Set(song.evaluations.map((ev) => ev.judge_name).filter(Boolean))].map((name, i) => (
                              <span key={i} style={{ fontSize: '0.82rem' }}>{name}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="a-desig-list">
                          {song.admin_placement_name ? (
                            <>
                              <DesigBadge name={song.admin_placement_name} />
                              <span style={{ fontSize: '0.7rem', color: '#6b7280', marginLeft: '0.25rem' }}>
                                (admin)
                              </span>
                            </>
                          ) : (
                            (song.designation_summary || []).map((d, i) => (
                              <DesigBadge key={i} name={d.category_name} />
                            ))
                          )}
                        </div>
                      </td>
                      <td>
                        {song.evaluations.length > 0 && (
                          <button
                            className="a-expand-btn"
                            onClick={() => toggleExpand(song.id)}
                            title={expanded[song.id] ? 'Hide details' : 'Show judge details'}
                          >
                            {expanded[song.id] ? '▲ Hide' : '▼ Details'}
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* ── Expanded breakdown row ── */}
                    {expanded[song.id] && (
                      <tr key={`${song.id}-exp`}>
                        <td colSpan={9} style={{ padding: 0, background: '#f9fafb' }}>
                          <div className="a-breakdown" style={{ padding: '1rem 1.5rem' }}>
                            <div style={{
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              color: '#6b7280',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              marginBottom: '0.75rem',
                            }}>
                              Judge Evaluations
                            </div>
                            {song.evaluations.map((ev) => (
                              <div key={ev.evaluation_id} className="a-eval-row">
                                <div className="a-eval-row__judge">
                                  {ev.judge_name}
                                  <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.2rem', flexWrap: 'wrap' }}>
                                    {ev.placement_category_name && <DesigBadge name={ev.placement_category_name} />}
                                    {!ev.is_submitted && (
                                      <span className="badge badge--pending">Draft</span>
                                    )}
                                  </div>
                                </div>
                                <div className="a-eval-row__scores">
                                  {ev.scores.length === 0 ? (
                                    <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>No scores recorded</span>
                                  ) : (
                                    ev.scores.map((sc) => (
                                      <span key={sc.criterion_id} className="a-score-chip">
                                        <span className="a-score-chip__name">{sc.criterion_name}</span>
                                        <span className="a-score-chip__value">{sc.score}</span>
                                      </span>
                                    ))
                                  )}
                                </div>
                                {ev.comments && (
                                  <p className="a-eval-row__comments">"{ev.comments}"</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: '32px' }}>
          <Link to="/admin/dashboard" className="c-back-btn">← Back to Dashboard</Link>
        </div>
      </main>
    </div>
  );
}
